import React, { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { Link, useLocation, useParams } from "react-router-dom";
import { startOfMonth, endOfMonth, format, subMonths } from "date-fns";

//primereact
import { Calendar } from "primereact/calendar";
import { Skeleton } from "primereact/skeleton";
import { BreadCrumb } from "primereact/breadcrumb";
// charts
import LineChart from "../../../../components/charts/LineChartSales";
import ColumnChartAccount from "../../../../components/charts/ColumnChartAccount";
import DonnutChart from "../../../../components/charts/DonnutChart";
import PieChartLand from "../../../../components/charts/PieChartLand";
import ColumnChartBill from "../../../../components/charts/ColumnChartBill";
import DonnutChartTeam from "../../../../components/charts/DonnutChartTeam";
import ColumnChartLand from "../../../../components/charts/ColumnChartLand";
import BarChartInfringementEnterprise from "../../../../components/charts/BarChartInfringementEnterprise";
import { getEnterpriseGraphics } from "../../../../services/Dashboard/getEnterpriseGraphics";
import ErrorMessage from "../../../../components/charts/ErrorMessage";

import { useAppContext } from "../../../../context/AppContext";
import usePermission from "../../../../hooks/usePermission";
import LineChartPayments from "../../../../components/charts/LineChartPayments";

export default function EnterpriseDashboard() {
  const { id } = useParams();
  const { state } = useLocation();
  const { nomenclatures, permissions } = useAppContext();
  const nomenclature = JSON.parse(nomenclatures);

  usePermission("17") // Visualizar Dashboard do Empreendimento
  const items = [
    {
      template: () => (
        <Link to={`/enterprise/${id}`} state={{name: state ? state.name : "Empreendimento"}} className="p-breadcrumb-link">
          Gestão de Vendas
        </Link>
      ),
    },
    {
      template: () => (
        <Link to={`/enterprise/dashboard/${id}`} state={{name: state ? state.name : "Empreendimento"}} className="p-breadcrumb-link">
          Dashboard
        </Link>
      ),
    },
  ];
  const home = {
    template: () => (
      <Link to={"/salesManagement"} className="p-breadcrumb-link">
        <i className="fa fa-home"></i>
      </Link>
    ),
  };

  const [dates, setDates] = useState([
    startOfMonth(subMonths(new Date(), 5)),
    endOfMonth(new Date()),
  ]);

  const [data, setData] = useState({
    enterprise_id: id,
    date_start: format(startOfMonth(subMonths(new Date(), 5)), "yyyy-MM-dd"),
    date_end: format(endOfMonth(new Date()), "yyyy-MM-dd"),
  });

  useEffect(() => {
    if (!dates.includes(null)) {
      const newData = {
        enterprise_id: id,
        date_start: format(dates[0], "yyyy-MM-dd"),
        date_end: format(dates[1], "yyyy-MM-dd"),
      };
      setData(newData);
    }
  }, [dates, id]);

  const {
    data: dataSales,
    isLoading: isLoadingSales,
    error: isError,
    refetch: refetchSales,
    isFetching: isFetchingSales
  } = useQuery({
    queryKey: [`dataSalesEnterprise${id}`],
    queryFn: () => getEnterpriseGraphics("sale", data),
    manual: true,
    staleTime: 60000,
    refetchOnWindowFocus: false,
    retry: false,
    enabled: !!permissions?.includes("17")

  });
  const {
    data: dataPaid,
    isLoading: isLoadingPaid,
    refetch: refetchPaid,
    isFetching: isFetchingPaid
  } = useQuery({
    queryKey: [`dataPaid${id}`],
    queryFn: () => getEnterpriseGraphics("future_paid", data),
    manual: true,
    staleTime: 60000,
    refetchOnWindowFocus: false,
    retry: false,
    enabled: !!permissions?.includes("17")
  });

  useEffect(() => {
    refetchSales();
    refetchPaid();
    refetchBilletMonthly();
    refetchTeamSales();
  }, [data]);

  const { data: dataNonPay, isLoading: isLoadingNonPay } = useQuery({
    queryKey: [`dataNonPay${id}`],
    queryFn: () => getEnterpriseGraphics("non_payment", data),
    manual: true,
    staleTime: 60000,
    refetchOnWindowFocus: false,
    retry: false,
    enabled: !!permissions?.includes("17")
  });

  const {
    data: dataLandStatus,
    isLoading: isLoadingLandStatus,
    isError: errorLandStatus
  } = useQuery({
    queryKey: [`dataLandStatus${id}`],
    queryFn: () => getEnterpriseGraphics("lands_status", data),
    manual: true,
    staleTime: 60000,
    refetchOnWindowFocus: false,
    retry: false,
    enabled: !!permissions?.includes("17")
  });

  const {
    data: dataLandPercentage,
    isLoading: isLoadingLandPercentage,
    isError: errorLandPercentage
  } = useQuery({
    queryKey: [`dataLandPercentage${id}`],
    queryFn: () => getEnterpriseGraphics("lands_percentage", data),
    manual: true,
    staleTime: 60000,
    refetchOnWindowFocus: false,
    retry: false,
    enabled: !!permissions?.includes("17")
  });

  const {
    data: dataBilletMonthly,
    isLoading: isLoadingBilletMonthly,
    refetch: refetchBilletMonthly
  } = useQuery({
    queryKey: [`dataBilletMonthly${id}`],
    queryFn: () => getEnterpriseGraphics("billet_monthly", data),
    manual: true,
    staleTime: 60000,
    refetchOnWindowFocus: false,
    retry: false,
    enabled: !!permissions?.includes("17")
  });

  const {
    data: dataTeamSales,
    isLoading: isLoadingTeamSales,
    refetch: refetchTeamSales,
    isError: errorTeamSales,
    isFetching: isFetchingTeamSales
  } = useQuery({
    queryKey: [`dataTeamSales${id}`],
    queryFn: () => getEnterpriseGraphics("team_sales", data),
    manual: true,
    staleTime: 60000,
    refetchOnWindowFocus: false,
    retry: false,
    enabled: !!permissions?.includes("17")
  });

  const { data: dataLandValue, isLoading: isLoadingLandValue } = useQuery({
    queryKey: [`dataLandValue${id}`],
    queryFn: () => getEnterpriseGraphics("lands_value", data),
    manual: true,
    staleTime: 60000,
    refetchOnWindowFocus: false,
    retry: false,
    enabled: !!permissions?.includes("17")
  });

  return (
    <>
      <div className="row justify-content-between">
        <div className="col-md-4">
          <BreadCrumb
            model={items}
            home={home}
            style={{
              background: "transparent",
              border: "none",
              fontSize: "16px",
            }}
          />
        </div>
        <div className="col-md-3 mb-3">
          <div className="card flex justify-content-center">
            <Calendar
              value={dates}
              
              onChange={(e) => {
                setDates(e.value);
              }}
              selectionMode="range"
              showIcon
              readOnlyInput
              locale="pt"
              dateFormat={"dd/mm/yy"}
              mask="99/99/9999"
              numberOfMonths={2}
            />
          </div>
        </div>
      </div>
      <div className="row p-3 mx-0 bg-white rounded">
        <div className="col-md-6 px-4 mt-2 rounded">
          <h4>Quadro Geral de Vendas</h4>
          {(isLoadingSales || isFetchingSales) ? (
            <div className="d-flex align-items-center justify-content-center">
              <Skeleton width="100%" height="220px"></Skeleton>
            </div>
          ) : (
            <LineChart data={dataSales} />
          )}
          {isError && <p>Ocorreu um erro</p>}
        </div>
        <div className="col-xl-3 col-md-6">
          <div className="titleChart pt-2 ">
            <h4 className="">Disponibilidade de {nomenclature[1].value}</h4>
          </div>
          {(isLoadingLandStatus) && (
            <div className="d-flex align-items-center justify-content-center">
              <Skeleton width="100%" height="220px"></Skeleton>
            </div>
          )}
          {errorLandStatus && <ErrorMessage />}
          {!isLoadingLandStatus && !errorLandStatus && (
            <DonnutChart data={dataLandStatus} />
          )}
        </div>
        <div className="col-xl-3 col-md-6">
          <div className="titleChart pt-2 ">
            <h4 className="">% de {nomenclature[1].value} disponíveis</h4>
          </div>
          {(isLoadingLandPercentage) && (
            <div className="d-flex align-items-center justify-content-center">
              <Skeleton width="100%" height="220px"></Skeleton>
            </div>
          )}
          {errorLandPercentage && <ErrorMessage />}
          {!isLoadingLandPercentage &&
            !errorLandPercentage &&
            (dataLandPercentage.every((item) => item.sales === 0) ? (
              <ErrorMessage />
            ) : (
              <PieChartLand data={dataLandPercentage} />
            ))}
        </div>
      </div>
      <div className="row p-3 mx-0 bg-white rounded mt-3">
        <div className="col-xl-6 p-2 text-start rounded">
          <h4>Inadimplência Geral</h4>
          {isLoadingNonPay ? (
            <div className="d-flex align-items-center justify-content-center">
              <Skeleton width="100%" height="220px"></Skeleton>
            </div>
          ) : (
            <BarChartInfringementEnterprise data={dataNonPay} />
          )}
        </div>
        <div className="col-md-6 px-4 mt-2 rounded">
          <h4>Pago x Em Aberto</h4>
          {(isLoadingPaid || isFetchingPaid) ? (
            <div className="d-flex align-items-center justify-content-center">
              <Skeleton width="100%" height="220px"></Skeleton>
            </div>
          ) : (
            <LineChartPayments data={dataPaid} />
          )}
        </div>
      </div>
      <div className="row row p-3 mx-0 bg-white rounded mt-3">
        <div className="col-xl-6 px-4 mt-2 rounded">
          <h4>Parcelas Mensais</h4>
          {isLoadingBilletMonthly || isLoadingBilletMonthly ? (
            <div className="d-flex align-items-center justify-content-center">
              <Skeleton width="100%" height="220px"></Skeleton>
            </div>
          ) : (
            <ColumnChartBill data={dataBilletMonthly} />
          )}
        </div>
        <div className="col-xl-3 col-md-6">
          <div className="titleChart pt-2 ">
            <h4 className="">Vendas por equipe</h4>
          </div>
          {(isLoadingTeamSales || isFetchingTeamSales) && (
            <div className="d-flex align-items-center justify-content-center">
              <Skeleton width="100%" height="220px"></Skeleton>
            </div>
          )}
          {errorTeamSales && <ErrorMessage />}
          {!isLoadingTeamSales && !errorTeamSales && !isFetchingTeamSales && (
            <DonnutChartTeam data={dataTeamSales} />
          )}
        </div>
        <div className="col-xl-3 col-md-6">
          <div className="titleChart pt-2 ">
            <h4 className="">Valor {nomenclature[1].value} Geral</h4>
          </div>
          {(isLoadingLandValue) ? (
            <div className="d-flex align-items-center justify-content-center">
              <Skeleton width="100%" height="220px"></Skeleton>
            </div>
          ) : (
            <ColumnChartLand data={dataLandValue} />
          )}
        </div>
      </div>
    </>
  );
}
